/* ==========================================================================
   Crumpaw · The Personalised Harness
   Buy box controller, live patch preview, and the small pieces of
   behaviour the content sections need (FAQ, patch messages, spare patches,
   size chart picking, colour picking).

   Everything lives inside this IIFE. Nothing is attached to `window` except
   `window.cphState`, which is the last broadcast state — a late-initialising
   section can read it instead of waiting for an event it has already missed.

   Degradation contract: if anything in here throws, the buy form keeps
   working. Colour and size are real radios inside the form with a <noscript>
   <select name="id"> fallback, the name and phone inputs carry their own
   `properties[...]` names, and Add to cart is never left disabled by a
   preview error.
   ========================================================================== */

(() => {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements) return;

  const STATE_EVENT = 'cph:state';
  const SELECT_EVENT = 'cph:select';
  const CART_UPDATE = 'cart:update';

  /* Attach-rate events. This store has no dataLayer, no gtag and no Web Pixel
     API on the page — only Shopify's own trekkie, which is not a bus you may
     publish to. So these are plain DOM events on document: nothing listens yet,
     nothing is loaded to make them work, and whatever gets chosen later
     subscribes without the block being reopened.

       cph:lead           { armed, colour, variantId, price }
       cph:lead-in-cart   { colour, variantId, price }        after it lands
       cph:lead-checkout  { present, colour, variantId }      on checkout click
  */
  const track = (name, detail) => {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  };

  /* ---------------------------------------------------------------- helpers */

  const MOBILE = '(max-width: 749px)';

  /* Breathing room between the pinned header and the top of the preview. */
  const PREVIEW_GAP = 12;

  const isMobile = () => window.matchMedia && window.matchMedia(MOBILE).matches;

  const wantsReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * How far down the viewport is covered by whatever the theme has pinned at
   * the top. The first <header> in the document is not necessarily the site
   * header — drawers and search panels have their own — so take the first one
   * that is actually pinned, and only while it is still on screen.
   *
   * Measured on each call rather than cached: this runs before the header
   * section has necessarily laid out, and a height of zero read once would be
   * wrong for the rest of the session.
   */
  const pinnedBottom = () => {
    const header = Array.from(document.querySelectorAll('header, cp-header')).find((node) => {
      const position = getComputedStyle(node).position;
      if (position !== 'sticky' && position !== 'fixed') return false;
      const box = node.getBoundingClientRect();
      return box.height > 0 && box.bottom > 0;
    });
    return header ? Math.round(header.getBoundingClientRect().bottom) : 0;
  };

  const readConfig = (root, attr) => {
    const node = root.querySelector(`[${attr}]`);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[cph] could not parse config', error);
      return null;
    }
  };

  /**
   * Minimal Shopify money formatter. Takes cents plus a money format string
   * such as "${{amount}}" and returns the formatted string. Falls back to a
   * plain dollar rendering if the format string is unusable.
   */
  const formatMoney = (cents, format) => {
    const amount = Number(cents) || 0;

    const withDelimiters = (value, precision, thousands, decimal) => {
      const fixed = (value / 100).toFixed(precision);
      const parts = fixed.split('.');
      const grouped = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${grouped}${decimal}${parts[1]}` : grouped;
    };

    if (typeof format !== 'string' || !format.includes('{{')) {
      return `$${withDelimiters(amount, 2, ',', '.')}`;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, token) => {
      switch (token) {
        case 'amount':
          return withDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(amount, 2, "'", '.');
        default:
          return withDelimiters(amount, 2, ',', '.');
      }
    });
  };

  /**
   * The shop's own money format without the cents when an amount is whole
   * dollars, so a saving reads "$18" rather than "$18.00".
   */
  const wholeMoneyFormat = (cents, format) => {
    if (Number(cents) % 100 !== 0 || typeof format !== 'string') return format;
    return format.replace(/\{\{\s*amount(_with_comma_separator)?\s*\}\}/, (_match, comma) =>
      comma ? '{{amount_no_decimals_with_comma_separator}}' : '{{amount_no_decimals}}'
    );
  };

  /** Fill {token} placeholders in an editor-provided string. */
  const interpolate = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
    );

  const pad2 = (n) => (n < 10 ? '0' : '') + n;

  const clamp = (min, value, max) => Math.max(min, Math.min(max, value));

  /* ------------------------------------------------- phone number grouping */

  const AU_GROUPS = [4, 3, 3];
  const AU_DIGITS = 10;
  const US_GROUPS = [3, 3, 4];
  const IS_US = !!window.Shopify && window.Shopify.country === 'US';

  /**
   * Digits only, with the international forms folded back to the local one so a
   * pasted "+61 487 558 825" ends up as 0487558825 rather than 6148755882.
   */
  const phoneDigits = (value) => {
    let digits = String(value == null ? '' : value).replace(/\D/g, '');
    if (IS_US) {
      return digits.replace(/^(?:001|1)/, '').slice(0, AU_DIGITS);
    }
    if (digits.startsWith('0061')) {
      digits = '0' + digits.slice(4);
    } else if (digits.startsWith('61') && digits.length > AU_DIGITS - 1) {
      digits = '0' + digits.slice(2);
    }
    return digits.slice(0, AU_DIGITS);
  };

  /** 4-3-3, filling progressively and never leaving a trailing space. */
  const formatPhone = (value) => {
    const digits = phoneDigits(value);
    const parts = [];
    let at = 0;
    for (const size of IS_US ? US_GROUPS : AU_GROUPS) {
      if (at >= digits.length) break;
      parts.push(digits.slice(at, at + size));
      at += size;
    }
    return parts.join(' ');
  };

  const digitsBefore = (value, caret) =>
    String(value).slice(0, caret).replace(/\D/g, '').length;

  /** Where the caret goes so it sits after the same digit it was after before. */
  const caretAfterDigits = (formatted, count) => {
    if (count <= 0) return 0;
    let seen = 0;
    for (let i = 0; i < formatted.length; i += 1) {
      const code = formatted.charCodeAt(i);
      if (code >= 48 && code <= 57) {
        seen += 1;
        if (seen === count) return i + 1;
      }
    }
    return formatted.length;
  };

  const setText = (node, value) => {
    if (node) node.textContent = value == null ? '' : String(value);
  };

  const show = (node, on) => {
    if (node) node.hidden = !on;
  };

  /**
   * POST to the Ajax cart and tell the rest of the theme about it. The cart
   * drawer listens for `cart:update` from a source other than its own and
   * re-renders from the server, so this is all the coupling there needs to be.
   */
  const readCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  /**
   * Take one line back out of the cart by its key.
   *
   * By key, not by variant id: the shopper may already have had that variant in
   * the cart before any of this, and removing theirs would be worse than the
   * problem being fixed.
   *
   * @param {string} key The line item key from /cart.js.
   */
  const removeLine = (key) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    }).then((r) => r.json());

  const addToCart = (items) =>
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((response) =>
        response.json().then((data) => {
          if (!response.ok) {
            const error = new Error(data && data.description ? data.description : 'Cart request failed');
            error.status = response.status;
            throw error;
          }
          return data;
        })
      )
      .then((data) => {
        document.dispatchEvent(
          new CustomEvent(CART_UPDATE, { bubbles: true, detail: { data: { source: 'cph' } } })
        );
        return data;
      });

  /* =========================================================================
     <cph-buybox> — the whole buy column plus the gallery it drives.
     ========================================================================= */

  class CphBuybox extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.config = readConfig(this, 'data-cph-config') || {};
      this.copy = this.config.copy || {};
      this.money = this.config.moneyFormat || '${{amount}}';

      this.refs = {
        variantId: this.querySelector('[data-cph="variant-id"]'),
        quantity: this.querySelector('[data-cph="quantity"]'),
        cta: this.querySelector('[data-cph="cta"]'),
        ctaLabel: this.querySelector('[data-cph="cta-label"]'),
        priceNow: this.querySelector('[data-cph="price-now"]'),
        priceWas: this.querySelector('[data-cph="price-was"]'),
        priceSave: this.querySelector('[data-cph="price-save"]'),
        priceSub: this.querySelector('[data-cph="price-sub"]'),
        stepMeta: this.querySelector('[data-cph="step-meta"]'),
        colourLabels: Array.from(this.querySelectorAll('[data-cph="colour-label"]')),
        sizeLabels: Array.from(this.querySelectorAll('[data-cph="size-label"]')),
        stockLeft: this.querySelector('[data-cph="stock-left"]'),
        stockPct: this.querySelector('[data-cph="stock-pct"]'),
        stockFill: this.querySelector('[data-cph="stock-fill"]'),
        stockWrap: this.querySelector('[data-cph="stock"]'),
        name: this.querySelector('[data-cph="name-input"]'),
        nameCount: this.querySelector('[data-cph="name-count"]'),
        nameHint: this.querySelector('[data-cph="name-hint"]'),
        phone: this.querySelector('[data-cph="phone-input"]'),
        phoneCount: this.querySelector('[data-cph="phone-count"]'),
        nameOnly: this.querySelector('[data-cph="name-only"]'),
        patches: Array.from(this.querySelectorAll('[data-cph="patch"]')),
        patchNames: Array.from(this.querySelectorAll('[data-cph="patch-name"]')),
        patchPhones: Array.from(this.querySelectorAll('[data-cph="patch-phone"]')),
        lead: this.querySelector('[data-cph="lead"]'),
        leadToggle: this.querySelector('[data-cph="lead-toggle"]'),
        leadPanel: this.querySelector('[data-cph="lead-panel"]'),
        leadTitle: this.querySelector('[data-cph="lead-title"]'),
        leadSub: this.querySelector('[data-cph="lead-sub"]'),
        leadLegend: this.querySelector('[data-cph="lead-legend"]'),
        leadChip: this.querySelector('[data-cph="lead-chip"]'),
        leadChipDot: this.querySelector('[data-cph="lead-chipdot"]'),
        leadChipText: this.querySelector('[data-cph="lead-chiptext"]'),
        leadZoom: this.querySelector('[data-cph="lead-zoom"]'),
        leadZoomImg: this.querySelector('[data-cph="lead-zoomimg"]'),
        leadRowThumb: this.querySelector('[data-cph="lead-rowthumb"]'),
        leadRowThumbImg: this.querySelector('[data-cph="lead-rowthumbimg"]'),
        step2Title: this.querySelector('[data-cph="step2-title"]'),
        dog2Card: this.querySelector('[data-cph="dog2-card"]'),
        dog2Name: this.querySelector('[data-cph="dog2-name"]'),
        dog2Phone: this.querySelector('[data-cph="dog2-phone"]'),
        dog2NameOnly: this.querySelector('[data-cph="dog2-name-only"]'),
        dog2NameCount: this.querySelector('[data-cph="dog2-name-count"]'),
        dog2NameHint: this.querySelector('[data-cph="dog2-name-hint"]'),
        dog2PhoneCount: this.querySelector('[data-cph="dog2-phone-count"]'),
        dog2PatchName: this.querySelector('[data-cph="dog2-patch-name"]'),
        dog2PatchPhone: this.querySelector('[data-cph="dog2-patch-phone"]'),
        spares: this.querySelector('[data-cph="spares"]'),
        spareRows: Array.from(this.querySelectorAll('[data-cph="spare-row"]')),
        sparesCount: this.querySelector('[data-cph="spares-count"]'),
        sparesHarness: this.querySelector('[data-cph="spares-harness"]'),
        sbSummary: this.querySelector('[data-cph="sb-summary"]'),
        measure: this.querySelector('[data-cph="measure"]'),
        measureToggle: this.querySelector('[data-cph="measure-toggle"]'),
        measureLink: this.querySelector('[data-cph="measure-link"]'),
        measureSign: this.querySelector('[data-cph="measure-sign"]'),
        error: this.querySelector('[data-cph="form-error"]'),
        // These live outside the buy box — in the gallery and further down the
        // page — so they are collected from the document.
        configLabels: Array.from(document.querySelectorAll('[data-cph="config-label"]')),
        dispatch: Array.from(document.querySelectorAll('[data-cph="dispatch-line"]')),
        cutoff: Array.from(document.querySelectorAll('[data-cph="cutoff-line"]')),
        livePrice: Array.from(document.querySelectorAll('[data-cph="live-price"]')),
        galleryFlag: document.querySelector('[data-cph="gallery-flag"]'),
      };

      this.colourInputs = Array.from(this.querySelectorAll('[data-cph="colour-input"]'));
      this.sizeInputs = Array.from(this.querySelectorAll('[data-cph="size-input"]'));
      this.tierInputs = Array.from(this.querySelectorAll('[data-cph="tier-input"]'));
      this.dog2Colours = Array.from(this.querySelectorAll('[data-cph="dog2-colour"]'));
      this.leadColours = Array.from(this.querySelectorAll('[data-cph="lead-colour"]'));
      this.dog2Sizes = Array.from(this.querySelectorAll('[data-cph="dog2-size"]'));

      this.gallery = document.querySelector('cph-gallery');

      this.bind();
      this.startClock();
      this.render();
    }

    disconnectedCallback() {
      if (this.timer) clearInterval(this.timer);
      if (this.observer) this.observer.disconnect();
      if (this.dockObserver) this.dockObserver.disconnect();
      if (this.onDockResize) window.removeEventListener('resize', this.onDockResize);
      document.removeEventListener(SELECT_EVENT, this.onSelect);
    }

    /* ------------------------------------------------------------ wiring */

    bind() {
      const rerender = () => this.render();

      // Picking a size or typing a name is the shopper engaging with the
      // product, so the opening lifestyle shot steps aside for the photograph
      // of what they have actually chosen.
      const release = () => {
        if (this.gallery && typeof this.gallery.releaseOpening === 'function') {
          this.gallery.releaseOpening(this.currentColour());
        }
      };

      this.colourInputs.forEach((input) => input.addEventListener('change', () => {
        this.render();
        if (this.gallery) this.gallery.showColour(input.value);
        this.revealPreview();
      }));
      this.sizeInputs.forEach((input) => input.addEventListener('change', () => {
        release();
        this.render();
      }));
      this.tierInputs.forEach((input) => input.addEventListener('change', rerender));

      if (this.refs.name) {
        this.refs.name.addEventListener('input', () => {
          release();
          this.render();
        });
      }
      this.releaseOpening = release;
      this.wirePhone();

      if (this.refs.nameOnly) {
        this.refs.nameOnly.addEventListener('change', () => {
          // Name only clears the number as well as disabling the field, so a
          // half-typed number can never be submitted as a line item property.
          if (this.refs.nameOnly.checked && this.refs.phone) this.refs.phone.value = '';
          this.render();
        });
      }

      this.wireMeasure();

      // Tier add-ons are separate products, so they cannot ride along on the one
      // product form. Arm them on click, then add them only once the harness
      // itself has actually landed in the cart — a form that fails validation or
      // a sold-out variant must not leave a stray add-on behind.
      /* A pair has to be two named harnesses before it can be bought.
         Letting the add through with the second name blank buys two of the
         same harness, and the shopper finds that out when the parcel arrives.
         So the click goes back to the second panel instead of to the cart.

         Capture, and on this element rather than the button: an ancestor's
         capture listener is guaranteed to run before both the arming handler
         below and product-form-component's own submit, so a blocked click
         cannot leave a lead or an add-on armed behind it. */
      this.addEventListener('click', (event) => {
        const cta = event.target.closest && event.target.closest('[data-cph="cta"]');
        if (!cta || this.dog2Ready()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.nudgeDog2();
      }, true);

      if (this.refs.cta) {
        this.refs.cta.addEventListener('click', () => {
          const tier = this.currentTier();
          this.pendingAddon = tier && tier.addonId && tier.addonQty ? tier : null;
          this.pendingDog2 = tier && Number(tier.quantity) > 1 ? this.dog2Line() : null;
          this.pendingLead = this.leadLine();
        });

        document.addEventListener(CART_UPDATE, (event) => {
          const source = event.detail && event.detail.data && event.detail.data.source;
          if (source === 'cph') return;
          const follow = [];
          if (this.pendingAddon) {
            follow.push({ id: this.pendingAddon.addonId, quantity: this.pendingAddon.addonQty });
          }
          if (this.pendingDog2) follow.push(this.pendingDog2);
          if (this.pendingLead) follow.push(this.pendingLead);
          this.pendingAddon = null;
          this.pendingDog2 = null;
          this.pendingLead = null;
          if (follow.length) this.addRest(follow);
        });
      }

      document.addEventListener('cph:media', () => this.render());

      this.onSelect = (event) => {
        const detail = event.detail || {};
        if (detail.colour) this.pick(this.colourInputs, detail.colour);
        if (detail.size) this.pick(this.sizeInputs, detail.size);
      };
      document.addEventListener(SELECT_EVENT, this.onSelect);

      this.wireColourWarming();
      this.wireSpares();
      this.wireDog2();
      this.wireLead();
      this.wireDock();
    }

    /**
     * The stage is one <img> whose src is rewritten on a colour change, so
     * every colour is a cold fetch the first time it is asked for. Warm the
     * photograph as soon as a cursor or a finger reaches its pill.
     *
     * The gallery owns the colour-to-media map, so the two are tied together
     * here rather than either of them knowing about the other. The shared
     * warmer sweeps whatever the shopper never hovers once the page is idle.
     */
    wireColourWarming() {
      const warmer = window.cpWarm;
      if (!warmer || !this.gallery || !this.gallery.map) return;

      this.colourInputs.forEach((input) => {
        const index = this.gallery.map[String(input.value).toLowerCase()];
        if (index == null) return;

        const thumb = document.querySelector(
          `[data-cph="thumb"][data-cph-index="${index}"]`
        );
        if (!thumb) return;

        const host = input.id ? this.querySelector(`label[for="${input.id}"]`) : null;
        if (!host) return;

        const warm = () => warmer.element(thumb);
        host.addEventListener('pointerenter', warm, { passive: true });
        host.addEventListener('touchstart', warm, { passive: true });
        input.addEventListener('focus', warm);
      });
    }

    /**
     * The measuring guide is one accordion with two ways in: its own header,
     * and the "How to measure" link beside the size label. The link only ever
     * opens — someone who taps it wants to see the guide, not to hide it — and
     * then brings it into view, because it sits below the size cards.
     */
    wireMeasure() {
      const panel = this.refs.measure;
      if (!panel) return;

      const setOpen = (open) => {
        panel.hidden = !open;
        const flag = open ? 'true' : 'false';
        if (this.refs.measureToggle) this.refs.measureToggle.setAttribute('aria-expanded', flag);
        if (this.refs.measureLink) this.refs.measureLink.setAttribute('aria-expanded', flag);
        setText(this.refs.measureSign, open ? '−' : '+');
      };

      if (this.refs.measureToggle) {
        this.refs.measureToggle.addEventListener('click', () => setOpen(panel.hidden));
      }

      if (this.refs.measureLink) {
        this.refs.measureLink.addEventListener('click', () => {
          setOpen(true);
          const item = panel.closest('.cph-faq__item') || panel;
          item.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    }

    /**
     * Pin a compact copy of the patch under the header while Step 2 is on
     * screen, so what is being typed stays visible while typing. Mirrors the
     * signature-bowl-v2-1 behaviour. The dock is only styled once this marks it
     * ready, so with the script off nothing shows rather than a broken frame.
     */
    /**
     * Bring the preview back into view after a colour change, on mobile only.
     *
     * Stacked on a phone the gallery stage sits at order 4 and the colour row
     * is inside the form at order 10, so picking a colour repaints a photograph
     * several screens above the thumb that just tapped. The customer gets no
     * feedback at all unless they scroll back up, which is the one thing a
     * colour picker has to give them.
     *
     * Skipped when most of the stage is already on screen, so tapping through
     * colours near the top of the page does not yank the viewport on every tap.
     * Skipped entirely on desktop, where the two columns sit side by side and
     * the preview never left.
     */
    revealPreview() {
      if (!isMobile()) return;

      const stage = this.gallery
        ? this.gallery.querySelector('.cph-gallery__stage')
        : document.querySelector('.cph-gallery__stage');
      if (!stage) return;

      const box = stage.getBoundingClientRect();
      if (!box.height) return;

      const top = pinnedBottom();
      const seen = Math.max(0, Math.min(box.bottom, window.innerHeight) - Math.max(box.top, top));
      if (seen >= box.height * 0.6) return;

      window.scrollTo({
        top: box.top + window.scrollY - top - PREVIEW_GAP,
        behavior: wantsReducedMotion() ? 'auto' : 'smooth',
      });
    }

    wireDock() {
      const dock = this.querySelector('[data-cph="dock"]');
      const step2 = this.querySelector('[data-cph="step2-card"]');
      if (!dock || !step2 || !('IntersectionObserver' in window)) return;

      dock.setAttribute('data-cph-ready', 'on');

      /* Sit under whatever sticky header the theme happens to be running. */
      const place = () => {
        const top = pinnedBottom();
        dock.style.setProperty('--cph-dock-top', `${top}px`);
        return top;
      };

      place();
      this.onDockResize = () => place();
      window.addEventListener('resize', this.onDockResize, { passive: true });

      this.dockObserver = new IntersectionObserver(
        (entries) => entries.forEach((e) => {
          if (e.isIntersecting) place();
          dock.classList.toggle('is-visible', e.isIntersecting);
        }),
        { threshold: 0, rootMargin: '-120px 0px -45% 0px' }
      );
      this.dockObserver.observe(step2);
    }

    /**
     * Group the number as it is typed. Formatting on input rather than on blur
     * means the patch preview and the line item property are already spaced,
     * so nobody reformats the number downstream.
     */
    wirePhone() {
      const input = this.refs.phone;
      if (!input) return;

      input.addEventListener('input', () => {
        const caret = digitsBefore(input.value, input.selectionStart);
        this.writePhone(formatPhone(input.value), caret);
      });

      // maxlength truncates a long paste before the value is ever seen, which
      // would cut "+61 487 558 825" down to something unrecognisable. Take the
      // clipboard text directly instead.
      input.addEventListener('paste', (event) => {
        const clipboard = event.clipboardData || window.clipboardData;
        if (!clipboard) return;
        event.preventDefault();
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const pasted = clipboard.getData('text') || '';
        const merged = input.value.slice(0, start) + pasted + input.value.slice(end);
        const caret = phoneDigits(input.value.slice(0, start) + pasted).length;
        this.writePhone(formatPhone(merged), caret);
      });

      // Backspace with the caret just after a space should take the digit on
      // the far side of it, not strand the caret on the separator.
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Backspace') return;
        const start = input.selectionStart;
        if (start !== input.selectionEnd || start === 0) return;
        if (input.value[start - 1] !== ' ') return;

        event.preventDefault();
        const before = digitsBefore(input.value, start);
        const digits = phoneDigits(input.value);
        this.writePhone(formatPhone(digits.slice(0, before - 1) + digits.slice(before)), before - 1);
      });
    }

    /** Write the value back and restore the caret by digit position. */
    writePhone(value, digitCount) {
      const input = this.refs.phone;
      // Every route into the phone field — typing, pasting, backspacing over a
      // space — lands here, so this is the one place the opening shot needs to
      // let go for the number as well as the name.
      if (this.releaseOpening) this.releaseOpening();
      input.value = value;
      const at = caretAfterDigits(value, digitCount);
      try {
        input.setSelectionRange(at, at);
      } catch (error) {
        /* type="tel" allows this everywhere that matters; never break render */
      }
      this.render();
    }

    /**
     * The pickers are visually hidden radios driven by their labels, so a
     * programmatic click does nothing useful — set `checked` and fire `change`
     * so every listener runs exactly as it would for a real click.
     */
    pick(inputs, value) {
      const wanted = String(value).toLowerCase();
      const match = inputs.find((input) => String(input.value).toLowerCase() === wanted);
      if (!match || match.disabled || match.checked) return;
      match.checked = true;
      match.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * The spare patch picker. Three rows, each either a preset or the shopper's
     * own wording — never both, because the patch only prints one line.
     *
     * The wording lives on a hidden input attached to the product form, so what
     * is on screen and what reaches the order are the same value rather than two
     * copies that can drift.
     */
    wireSpares() {
      if (!this.refs.spares || !this.refs.spareRows.length) return;

      this.refs.spareRows.forEach((row) => {
        const toggle = row.querySelector('[data-cph="spare-toggle"]');
        const input = row.querySelector('[data-cph="spare-input"]');
        const presets = Array.from(row.querySelectorAll('[data-cph="spare-preset"]'));

        if (toggle) {
          toggle.addEventListener('click', () => {
            const open = row.dataset.cphOpen === 'true';
            this.openSpare(open ? null : row);
          });
        }

        if (input) {
          input.addEventListener('input', () => {
            // Typing is the shopper overriding the preset they may have picked,
            // so the preset selection goes with it.
            this.setSpare(row, input.value, null);
          });
        }

        presets.forEach((button) => {
          button.addEventListener('click', () => {
            this.setSpare(row, button.dataset.cphValue, button);
            if (input) input.value = '';
            this.openSpare(null);
          });
        });
      });
    }

    /**
     * Open one row and close the rest. Passing null closes everything.
     *
     * @param {Element|null} target
     */
    openSpare(target) {
      this.refs.spareRows.forEach((row) => {
        const open = row === target;
        const panel = row.querySelector('[data-cph="spare-panel"]');
        const toggle = row.querySelector('[data-cph="spare-toggle"]');
        row.dataset.cphOpen = open ? 'true' : 'false';
        if (panel) panel.hidden = !open;
        if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      this.render();
      if (target) {
        const input = target.querySelector('[data-cph="spare-input"]');
        if (input) input.focus({ preventScroll: true });
      }
    }

    /**
     * Put a wording on a row. Uppercased, because that is how the patch prints.
     *
     * @param {Element} row
     * @param {string} value
     * @param {Element|null} preset The preset button chosen, if it was a preset.
     */
    setSpare(row, value, preset) {
      const hidden = row.querySelector('[data-cph="spare-value"]');
      const clean = String(value == null ? '' : value)
        .toUpperCase()
        .slice(0, this.config.spareMax || 11);
      if (hidden) hidden.value = clean;

      row.querySelectorAll('[data-cph="spare-preset"]').forEach((button) => {
        button.classList.toggle('is-on', button === preset);
      });

      this.render();
    }

    /** What each row currently holds, in row order. */
    spareValues() {
      return this.refs.spareRows.map((row) => {
        const hidden = row.querySelector('[data-cph="spare-value"]');
        return hidden ? String(hidden.value || '').trim() : '';
      });
    }

    /**
     * Paint the picker: visibility, row states, the counter, the footer line,
     * and the summary the sticky bar reads.
     *
     * @param {object} tier The tier the shopper has chosen.
     * @param {string} patchLabel What the harness's own patch will say.
     */
    renderSpares(tier, patchLabel) {
      const card = this.refs.spares;
      if (!card || !this.refs.spareRows.length) return;

      // The picker configures the add-on, so it belongs to whichever tier
      // actually carries one.
      const applies = !!(tier && Number(tier.addonQty) > 0);
      card.hidden = !applies;

      const values = this.spareValues();
      this.refs.spareRows.forEach((row, index) => {
        const value = values[index];
        const filled = value.length > 0;
        const open = row.dataset.cphOpen === 'true';
        const label = row.querySelector('[data-cph="spare-label"]');
        const toggle = row.querySelector('[data-cph="spare-toggle"]');
        const counter = row.querySelector('[data-cph="spare-counter"]');
        const input = row.querySelector('[data-cph="spare-input"]');
        const hidden = row.querySelector('[data-cph="spare-value"]');

        row.classList.toggle('is-filled', filled);
        row.classList.toggle('is-open', open);
        // The empty label carries the row number, so each row names itself
        // rather than three rows repeating one word.
        setText(label, filled ? value : interpolate(this.copy.spareEmpty, { n: index + 1 }));
        setText(
          toggle,
          open ? this.copy.spareClose : filled ? this.copy.spareChange : this.copy.spareSet
        );
        if (counter && input) {
          setText(counter, `${input.value.length}/${this.config.spareMax || 11}`);
        }
        // Off the 3-pack the rows post nothing at all; on it, an untouched row
        // posts an empty string so fulfilment can see it was left blank.
        if (hidden) hidden.disabled = !applies;
      });

      const set = values.filter(Boolean).length;
      const total = this.refs.spareRows.length;
      setText(this.refs.sparesCount, interpolate(this.copy.sparesCount, { set, total }));
      setText(this.refs.sparesHarness, patchLabel);

      if (!applies) return '';
      return set >= total ? this.copy.sparesDone : this.copy.sparesTodo;
    }

    /**
     * The rest of the order — the second harness, the spare patches — goes in as
     * its own line after the form's own add has landed.
     *
     * If any of it fails, the first line comes back out. A cart holding one
     * harness when the shopper was shown a price for two is worse than a cart
     * holding nothing: the price they agreed to and the price they would pay no
     * longer match, and nothing on the page would say so. Rolling back leaves
     * them exactly where they started, with the form still filled in.
     *
     * @param {Array<object>} items Line items for /cart/add.js.
     */
    addRest(items) {
      readCart()
        .then((cart) => {
          // The line the form just added, so it can be undone by key.
          const line = (cart.items || []).find(
            (item) => String(item.product_id) === String(this.config.productId)
          );
          const key = line ? line.key : null;
          return addToCart(items).catch((error) => {
            if (!key) throw error;
            return removeLine(key).then(() => {
              throw error;
            });
          });
        })
        .then(() => {
          setText(this.refs.error, '');
          const line = items.find((i) => String(i.id) === String((this.leadLine() || {}).id));
          if (line) {
            this.leadInCart = true;
            track('cph:lead-in-cart', {
              colour: this.leadColour(),
              variantId: line.id,
              price: Number((this.config.lead || {}).price) || 0,
            });
          }
        })
        .catch(() => {
          setText(this.refs.error, this.copy.dog2Failed || '');
          document.dispatchEvent(
            new CustomEvent(CART_UPDATE, { bubbles: true, detail: { data: { source: 'cph' } } })
          );
        });
    }

    /** The second harness as a cart line, or null if there is nothing to add. */
    dog2Line() {
      const variant = this.dog2Variant();
      if (!variant) return null;
      const name = String((this.refs.dog2Name && this.refs.dog2Name.value) || '')
        .toUpperCase()
        .slice(0, this.config.nameMax || 10);
      const nameOnly = !!(this.refs.dog2NameOnly && this.refs.dog2NameOnly.checked);
      const phone = nameOnly
        ? ''
        : String((this.refs.dog2Phone && this.refs.dog2Phone.value) || '');
      const properties = {};
      // Same keys as harness 1, because each line is one harness for one dog.
      properties[this.config.propertyName || 'Pet Name'] = name;
      properties[this.config.propertyPhone || 'Phone'] = phone;
      return { id: variant.id, quantity: 1, properties };
    }

    dog2Colour() {
      const input = this.dog2Colours.find((i) => i.checked);
      return input ? input.value : null;
    }

    dog2Size() {
      const input = this.dog2Sizes.find((i) => i.checked);
      return input ? input.value : null;
    }

    /** What the pack discount takes off `qty` harnesses, in cents. One harness gets nothing. */
    packOff(qty) {
      return qty > 1 ? this.packOffEach() * qty : 0;
    }

    /**
     * The pack discount is one fixed amount in the store currency, and Shopify
     * converts it at the market's rate and rounds it to the cent. Working it
     * out the same way keeps the page and the cart on one figure in every
     * market, including ones added later. A config that already carries a
     * converted packOffEach (cp-hn-de.js) wins.
     */
    packOffEach() {
      const converted = Number(this.config.packOffEach) || 0;
      if (converted > 0) return converted;
      const currency = window.Shopify && window.Shopify.currency;
      const rate = (currency && Number(currency.rate)) || 1;
      return Math.round((Number(this.config.packOffEachShop) || 0) * rate + 1e-9);
    }

    dog2Variant() {
      const colour = String(this.dog2Colour() || '').toLowerCase();
      const size = String(this.dog2Size() || '').toLowerCase();
      return (this.config.variants || []).find(
        (v) => String(v.colour).toLowerCase() === colour && String(v.size).toLowerCase() === size
      );
    }

    /**
     * The second panel. Same fields and the same rules as Step 2, so the two
     * behave identically; only the values are its own.
     */
    wireDog2() {
      if (!this.refs.dog2Card) return;
      const rerender = () => this.render();

      this.dog2Colours.forEach((i) => i.addEventListener('change', rerender));
      this.dog2Sizes.forEach((i) => i.addEventListener('change', rerender));
      if (this.refs.dog2Name) this.refs.dog2Name.addEventListener('input', rerender);

      if (this.refs.dog2NameOnly) {
        this.refs.dog2NameOnly.addEventListener('change', () => {
          if (this.refs.dog2NameOnly.checked && this.refs.dog2Phone) {
            this.refs.dog2Phone.value = '';
          }
          this.render();
        });
      }

      // The same 4-3-3 grouping as harness 1, through the same helpers.
      const phone = this.refs.dog2Phone;
      if (phone) {
        const write = (value, digitCount) => {
          phone.value = value;
          const at = caretAfterDigits(value, digitCount);
          try {
            phone.setSelectionRange(at, at);
          } catch (error) {
            /* a detached or hidden field cannot take a caret */
          }
          this.render();
        };

        phone.addEventListener('input', () => {
          write(formatPhone(phone.value), digitsBefore(phone.value, phone.selectionStart));
        });

        phone.addEventListener('paste', (event) => {
          const clipboard = event.clipboardData || window.clipboardData;
          if (!clipboard) return;
          event.preventDefault();
          const start = phone.selectionStart;
          const end = phone.selectionEnd;
          const pasted = clipboard.getData('text') || '';
          const merged = phone.value.slice(0, start) + pasted + phone.value.slice(end);
          write(formatPhone(merged), phoneDigits(phone.value.slice(0, start) + pasted).length);
        });

        phone.addEventListener('keydown', (event) => {
          if (event.key !== 'Backspace') return;
          const start = phone.selectionStart;
          if (start !== phone.selectionEnd || start === 0) return;
          if (phone.value[start - 1] !== ' ') return;
          event.preventDefault();
          const before = digitsBefore(phone.value, start);
          const digits = phoneDigits(phone.value);
          write(formatPhone(digits.slice(0, before - 1) + digits.slice(before)), before - 1);
        });
      }
    }

    /**
     * Paint the second panel and the heading that distinguishes the two.
     *
     * @param {object} tier The chosen tier.
     */
    renderDog2(tier) {
      const paired = !!(tier && Number(tier.quantity) > 1);
      if (this.refs.dog2Card) this.refs.dog2Card.hidden = !paired;

      // Step 2 only needs naming when there is a second one to tell it from.
      if (this.refs.step2Title) {
        setText(
          this.refs.step2Title,
          paired ? this.copy.step2TitlePaired : this.copy.step2Title
        );
      }

      const name = String((this.refs.dog2Name && this.refs.dog2Name.value) || '')
        .toUpperCase()
        .slice(0, this.config.nameMax || 10);
      const nameOnly = !!(this.refs.dog2NameOnly && this.refs.dog2NameOnly.checked);
      const phone = nameOnly
        ? ''
        : String((this.refs.dog2Phone && this.refs.dog2Phone.value) || '');

      if (this.refs.dog2Phone) this.refs.dog2Phone.disabled = nameOnly;
      setText(this.refs.dog2NameCount, `${name.length}/${this.config.nameMax || 10}`);
      setText(this.refs.dog2PhoneCount, `${phone.length}/${this.config.phoneMax || 12}`);

      /* Once a name is being typed the panel stops being the thing in the way,
         so the warning comes off with the first keystroke rather than staying
         up until the next add. */
      if (name && this.refs.dog2Card) this.refs.dog2Card.classList.remove('is-missing');
      setText(
        this.refs.dog2NameHint,
        name
          ? this.copy.hintValid
          : this.refs.dog2Card && this.refs.dog2Card.classList.contains('is-missing')
            ? this.copy.dog2NameRequired || this.copy.hintEmpty
            : this.copy.hintEmpty
      );
      setText(this.refs.dog2PatchName, name || this.copy.namePlaceholder);
      setText(this.refs.dog2PatchPhone, phone || this.copy.phonePlaceholder);
      show(this.refs.dog2PatchPhone, !nameOnly);

      return { paired, name };
    }

    /**
     * Is the second harness named, when there is a second harness?
     *
     * Only the name is required, matching harness 1: the panel offers a name
     * only option, so demanding a number here would contradict a choice the
     * page itself puts in front of the shopper.
     */
    dog2Ready() {
      const tier = this.currentTier();
      if (!tier || Number(tier.quantity) < 2) return true;
      return !!String((this.refs.dog2Name && this.refs.dog2Name.value) || '').trim();
    }

    /**
     * Send the shopper back to the second harness rather than to the cart.
     *
     * Scroll, then say what is missing, then take the caret - with
     * preventScroll, because focus does its own jump and the two together land
     * somewhere neither of them meant.
     */
    nudgeDog2() {
      const card = this.refs.dog2Card;
      if (card) {
        card.classList.add('is-missing');
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setText(this.refs.dog2NameHint, this.copy.dog2NameRequired || this.copy.hintEmpty || '');
      if (this.refs.dog2Name) {
        try {
          this.refs.dog2Name.focus({ preventScroll: true });
        } catch (error) {
          this.refs.dog2Name.focus();
        }
      }
      track('cph:dog2-blocked', { tier: (this.currentTier() || {}).title || '' });
    }

    /** Has the shopper ticked the lead on? */
    leadArmed() {
      return !!(this.refs.leadToggle && this.refs.leadToggle.checked);
    }

    /** The chosen lead colour, or the first one if somehow none is checked. */
    leadColour() {
      const input = this.leadColours.find((i) => i.checked) || this.leadColours[0];
      return input ? input.value : '';
    }

    /**
     * The lead as a cart line, or null when it is not armed.
     *
     * Ticking the box does not touch the cart — nothing is in the cart until the
     * harness is. It arms the line, which then rides the same follow-up call as
     * the second harness and the spare patches, so the shopper presses one
     * button and gets both items.
     */
    leadLine() {
      if (!this.leadArmed()) return null;
      const map = (this.config.lead || {}).variants || {};
      const id = map[this.leadColour()];
      return id ? { id, quantity: 1 } : null;
    }

    wireLead() {
      if (!this.refs.lead) return;
      const rerender = () => this.render();
      const report = () => {
        const armed = this.leadArmed();
        const line = this.leadLine();
        track('cph:lead', {
          armed,
          colour: this.leadColour(),
          variantId: line ? line.id : null,
          price: Number((this.config.lead || {}).price) || 0,
        });
      };

      if (this.refs.leadToggle) {
        this.refs.leadToggle.addEventListener('change', () => {
          rerender();
          report();
        });
      }
      this.leadColours.forEach((input) =>
        input.addEventListener('change', () => {
          rerender();
          if (this.leadArmed()) report();
        })
      );

      /* Checkout initiation is not something this page owns — the buttons live
         in the drawer and the sticky bar — so listen for any of them rather
         than reaching into either. */
      document.addEventListener('click', (event) => {
        const node = event.target;
        if (!node || !node.closest) return;
        if (!node.closest('[name="checkout"], a[href*="/checkout"], [data-cp-sb-sticky="cta"]')) return;
        const line = this.leadLine();
        track('cph:lead-checkout', {
          present: !!this.leadInCart,
          colour: this.leadColour(),
          variantId: line ? line.id : null,
        });
      }, true);

      /* The photo in the row and the one in the open panel enlarge the same
         way, and each hands focus back to whichever one was pressed. */
      [this.refs.leadZoom, this.refs.leadRowThumb].forEach((trigger) => {
        if (trigger) trigger.addEventListener('click', () => this.openLeadLightbox(trigger));
      });
    }

    /** Enlarge the lead photos, opening on the colour the shopper has chosen. */
    openLeadLightbox(returnTo) {
      const box = document.querySelector('cph-lightbox');
      if (!box || typeof box.openSingle !== 'function') return;
      const copy = this.config.lead || {};

      // Every lead colour that has a photograph, in picker order, so the
      // arrows walk the lead's own colours. Stepping on used to fall through
      // to the product gallery and show the harness instead, which is not a
      // lead at all and left no way back to the one being looked at.
      const frames = this.leadColours
        .filter((input) => input.dataset.cphFull)
        .map((input) => ({
          src: input.dataset.cphFull,
          alt: input.dataset.cphAlt || '',
          title: interpolate(copy.titleTemplate, { colour: input.value }),
          w: Number(input.dataset.cphW),
          h: Number(input.dataset.cphH),
        }));
      if (!frames.length) return;

      // Open on the colour the shopper has actually chosen, not the first.
      const chosen = this.leadColours.find((i) => i.checked);
      const at = chosen
        ? frames.findIndex((frame) => frame.src === chosen.dataset.cphFull)
        : 0;

      box.openSingle({
        items: frames,
        index: at < 0 ? 0 : at,
        caption: copy.caption || '',
        returnTo,
      });
    }

    /**
     * Paint the lead row. With no photo set the thumbnail and the enlarge
     * affordance simply are not there — the row still ticks, prices and adds,
     * because the picture is a nicety and the lead is the product.
     */
    renderLead(harnessColour, harnessSwatch) {
      if (!this.refs.lead) return;
      const copy = this.config.lead || {};
      const armed = this.leadArmed();
      const colour = this.leadColour();

      this.refs.lead.classList.toggle('is-on', armed);
      if (this.refs.leadPanel) this.refs.leadPanel.hidden = !armed;

      setText(this.refs.leadTitle, armed ? copy.titleOn : copy.titleOff);
      setText(
        this.refs.leadSub,
        armed ? interpolate(copy.subOn, { colour }) : copy.subOff
      );
      setText(
        this.refs.leadLegend,
        interpolate(copy.colourLabel, { colour: String(colour).toUpperCase() })
      );

      /* Context only. The harness colour is shown so the shopper can see it
         while choosing, and never constrains what they may choose. */
      setText(this.refs.leadChipText, interpolate(copy.chipTemplate, { colour: harnessColour }));
      if (this.refs.leadChipDot && harnessSwatch) {
        this.refs.leadChipDot.style.background = harnessSwatch;
      }

      const input = this.leadColours.find((i) => i.checked) || this.leadColours[0];

      /* A colour with neither a metafield nor an entry in the built-in map gets
         a neutral dot, which on its own is indistinguishable from a deliberate
         grey. Say so, once, rather than letting a swatch picker quietly show
         seven identical dots. */
      if (input && !input.dataset.cphHex && !this.warnedSwatch) {
        this.warnedSwatch = true;
        const missing = this.leadColours.filter((i) => !i.dataset.cphHex).map((i) => i.value);
        // eslint-disable-next-line no-console
        console.warn(
          '[cph] Lead colours with no swatch colour, showing neutral:',
          missing.join(', '),
          '— set custom.swatch_hex on the variant, or add the name to the built-in map.'
        );
      }

      const thumb = input ? input.dataset.cphThumb : '';
      const has = !!thumb;
      if (this.refs.leadZoom) this.refs.leadZoom.hidden = !has;
      if (this.refs.leadRowThumb) this.refs.leadRowThumb.hidden = !has;
      if (this.refs.leadRowThumbImg && has) {
        this.refs.leadRowThumbImg.style.backgroundImage = `url("${thumb}")`;
      }
      if (this.refs.leadZoomImg && has) {
        this.refs.leadZoomImg.style.backgroundImage = `url("${thumb}")`;
      }
    }

    /* ------------------------------------------------------------- clock */

    startClock() {
      const hour = Number(this.config.cutoffHour);
      if (!Number.isFinite(hour)) return;

      const tick = () => {
        const now = new Date();
        const end = new Date(now);
        end.setHours(hour, 0, 0, 0);
        const today = end > now;
        if (!today) end.setDate(end.getDate() + 1);
        this.secs = Math.max(0, Math.floor((end - now) / 1000));
        this.sameDay = today;
        this.renderClock();
      };

      tick();
      this.timer = setInterval(tick, 1000);
    }

    renderClock() {
      const h = Math.floor(this.secs / 3600);
      const m = Math.floor((this.secs % 3600) / 60);
      const s = this.secs % 60;
      const cutoff = `${h}h ${pad2(m)}m ${pad2(s)}s`;

      const cutoffLine = this.sameDay
        ? interpolate(this.copy.cutoffToday, { cutoff })
        : interpolate(this.copy.cutoffPassed, { cutoff });
      const dispatchLine = this.sameDay
        ? interpolate(this.copy.dispatchToday, { cutoff })
        : interpolate(this.copy.dispatchPassed, { cutoff });

      this.refs.cutoff.forEach((node) => setText(node, cutoffLine));
      this.refs.dispatch.forEach((node) => setText(node, dispatchLine));
    }

    /* ------------------------------------------------------------- state */

    currentColour() {
      const input = this.colourInputs.find((i) => i.checked);
      return input ? input.value : null;
    }

    currentSize() {
      const input = this.sizeInputs.find((i) => i.checked);
      return input ? input.value : null;
    }

    currentTier() {
      const input = this.tierInputs.find((i) => i.checked);
      if (!input) return this.config.tiers && this.config.tiers[0];
      return (this.config.tiers || []).find((t) => String(t.id) === String(input.value));
    }

    /**
     * Resolve the variant, degrading one axis at a time. A size card whose
     * value does not correspond to any option value on the product must not
     * throw away a colour the shopper did choose — half a match beats none.
     */
    currentVariant() {
      const variants = this.config.variants || [];
      if (!variants.length) return null;

      const same = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
      const colour = this.currentColour();
      const size = this.currentSize();

      return (
        (colour && size && variants.find((v) => same(v.colour, colour) && same(v.size, size))) ||
        (colour && variants.find((v) => same(v.colour, colour) && v.available)) ||
        (colour && variants.find((v) => same(v.colour, colour))) ||
        (size && variants.find((v) => same(v.size, size) && v.available)) ||
        (size && variants.find((v) => same(v.size, size))) ||
        variants.find((v) => v.available) ||
        variants[0]
      );
    }

    /* ------------------------------------------------------------ render */

    render() {
      // No product bound to the section: leave every summary the server
      // rendered alone rather than overwriting it with zeroes.
      if (!(this.config.variants || []).length) return;

      const variant = this.currentVariant();
      const tier = this.currentTier() || {};
      const qty = Math.max(1, Number(tier.quantity) || 1);

      const name = String((this.refs.name && this.refs.name.value) || '')
        .toUpperCase()
        .slice(0, this.config.nameMax || 10);
      const nameOnly = !!(this.refs.nameOnly && this.refs.nameOnly.checked);
      const phone = nameOnly ? '' : String((this.refs.phone && this.refs.phone.value) || '').slice(0, this.config.phoneMax || 12);

      /* ---- variant ---- */
      if (variant && this.refs.variantId) this.refs.variantId.value = String(variant.id);
      // The form only ever carries harness 1; anything else is its own line.
      if (this.refs.quantity) this.refs.quantity.value = "1";

      /* ---- availability of each size for the chosen colour ---- */
      const colour = this.currentColour();
      if (this.config.hideUnavailable) {
        this.sizeInputs.forEach((input) => {
          const combo = (this.config.variants || []).find(
            (v) =>
              String(v.colour).toLowerCase() === String(colour).toLowerCase() &&
              String(v.size).toLowerCase() === String(input.value).toLowerCase()
          );
          input.disabled = !!combo && !combo.available;
        });
      }

      /* ---- price ---- */
      const unit = variant ? variant.price : 0;
      const compare = variant && variant.compareAt > unit ? variant.compareAt : 0;
      const addon = Number(tier.addonPrice) || 0;
      const addonQty = Number(tier.addonQty) || 0;

      /* A paired bundle is two separate harnesses, each with its own variant, so
         the price is the two added together. They are the same money today, but
         summing them is what keeps the figure honest if they ever are not. */
      const second = qty > 1 ? this.dog2Variant() : null;
      const secondUnit = second ? second.price : unit * (qty - 1);
      const secondCompare = second && second.compareAt > second.price ? second.compareAt : 0;

      /* An armed lead is money the button would charge, so the figure on the
         page counts it from the moment the box is ticked. */
      const leadPrice = this.leadArmed() ? Number((this.config.lead || {}).price) || 0 : 0;

      /* A pack price replaces what the harnesses themselves come to, and only
         that: an armed lead and any add-on are separate products and are still
         charged on top. `was` is left on the compare-at prices, so the saving
         is measured against what the harnesses would cost apart rather than
         against the pack price itself. */
      const packPrice = Number(tier.packPrice) || 0;
      /* The two-harness pack is an automatic discount of a fixed amount off
         each harness, not a flat price, so its figure is the two sizes
         actually chosen less that amount per harness. */
      const packOff = this.packOff(qty);
      const harnesses = packOff > 0
        ? unit + secondUnit - packOff
        : packPrice > 0 ? packPrice : unit + secondUnit;

      const now = harnesses + addon * addonQty + leadPrice;
      const was = (compare || unit) + (secondCompare || secondUnit) + addon * addonQty + leadPrice;
      const hasSaving = was > now;

      setText(this.refs.priceNow, formatMoney(now, this.money));
      setText(this.refs.priceWas, formatMoney(was, this.money));
      show(this.refs.priceWas, hasSaving);
      const savedPct = was > 0 ? Math.round(((was - now) / was) * 100) : 0;
      setText(
        this.refs.priceSave,
        interpolate(this.copy.save, { amount: formatMoney(was - now, this.money), percent: savedPct })
      );
      show(this.refs.priceSave, hasSaving);

      this.refs.livePrice.forEach((node) => setText(node, formatMoney(now, this.money)));

      const instalment = formatMoney(Math.round(now / 4), this.money);
      setText(
        this.refs.priceSub,
        interpolate(this.copy.priceSub, { pack: tier.packLabel || '', instalment })
      );

      /* ---- tier prices ---- */
      (this.config.tiers || []).forEach((t) => {
        const tq = Math.max(1, Number(t.quantity) || 1);
        const tAddon = (Number(t.addonPrice) || 0) * (Number(t.addonQty) || 0);
        const tPack = Number(t.packPrice) || 0;
        const tOff = this.packOff(tq);
        // Same sum as the button: the second dog's own size, when there is one.
        const tSecond = tq > 1 ? this.dog2Variant() : null;
        const tUnits = unit + (tSecond ? tSecond.price : unit * (tq - 1));
        const tNow = (tOff > 0 ? tUnits - tOff : tPack > 0 ? tPack : tUnits) + tAddon;
        const tSecondCompare = tSecond && tSecond.compareAt > tSecond.price ? tSecond.compareAt : 0;
        const tWas = (tSecond ? (compare || unit) + (tSecondCompare || tSecond.price) : (compare || unit) * tq) + tAddon;
        const priceNode = this.querySelector(`[data-cph-tier-price="${t.id}"]`);
        const saveNode = this.querySelector(`[data-cph-tier-save="${t.id}"]`);
        setText(priceNode, formatMoney(tNow, this.money));
        if (saveNode) {
          if (tOff > 0) {
            setText(saveNode, interpolate(this.copy.packSave || this.copy.save, {
              amount: formatMoney(tOff, wholeMoneyFormat(tOff, this.money)),
              percent: tUnits > 0 ? Math.round((tOff / tUnits) * 100) : 0,
            }));
          } else if (tWas > tNow) {
            const tPct = tWas > 0 ? Math.round(((tWas - tNow) / tWas) * 100) : 0;
            setText(saveNode, interpolate(this.copy.save, {
              amount: formatMoney(tWas - tNow, this.money), percent: tPct,
            }));
          } else {
            setText(saveNode, tq > 1 ? '' : t.noSaveLabel || '');
          }
        }
      });

      /* ---- stock ---- */
      const stock = variant ? Number(variant.stock) : null;
      const batch = Number(this.config.stockBatch) || 60;
      if (this.refs.stockWrap) {
        const known = Number.isFinite(stock) && stock > 0;
        show(this.refs.stockWrap, known);
        if (known) {
          const claimed = Math.max(28, Math.min(96, Math.round(((batch - stock) / batch) * 100)));
          setText(
            this.refs.stockLeft,
            interpolate(this.copy.stockLeft, {
              count: stock,
              colour: variant.colour || '',
              size: variant.size || '',
            })
          );
          setText(this.refs.stockPct, interpolate(this.copy.stockClaimed, { pct: `${claimed}%` }));
          if (this.refs.stockFill) this.refs.stockFill.style.width = `${claimed}%`;
        }
      }

      /* ---- labels ---- */
      const patchLabel = nameOnly
        ? interpolate(this.copy.patchNameOnly, { name: name || this.copy.namePlaceholder })
        : `${name || this.copy.namePlaceholder} · ${phone || this.copy.phonePlaceholder}`;

      /* The spare patches sit under the bundle that pays for them, and the
         footer line shows the harness's own wording beside them so nobody
         orders the same words twice. */
      const leadDot = this.colourInputs.find((i) => i.checked);
      this.renderLead(
        colour || (variant && variant.colour) || "",
        leadDot ? (leadDot.dataset.cphSwatch || "") : ""
      );
      const spareSummary = this.renderSpares(tier, patchLabel);
      const pair = this.renderDog2(tier);

      /* One line for the sticky bar to read. A second dog with no name is the
         thing most worth saying, so it wins over the patch count. */
      if (this.refs.sbSummary) {
        let summary = spareSummary;
        if (pair.paired) {
          summary = pair.name
            ? `${name || this.copy.namePlaceholder} + ${pair.name}`
            : this.copy.dog2Todo;
        }
        setText(this.refs.sbSummary, summary || '');
      }

      /* The chip reports what the shopper picked, not what the variant lookup
         settled on. The two agree whenever the combination exists; when it does
         not, the shopper's own choice is the honest thing to show. */
      const sizeChoice = this.currentSize();
      const configLabel = interpolate(this.copy.configLabel, {
        colour: colour || (variant && variant.colour) || '',
        size: sizeChoice || (variant && variant.size) || '',
        patch: patchLabel,
      });
      /* The overlays describe the configuration, so they belong on the main
         image and on the variant images — not on the diagrams and detail shots
         the rest of the gallery holds. */
      const overlaysApply =
        this.gallery && typeof this.gallery.isShowingOverlayMedia === 'function'
          ? this.gallery.isShowingOverlayMedia()
          : false;

      this.refs.configLabels.forEach((node) => {
        setText(node, configLabel);
        show(node, overlaysApply);
      });
      show(this.refs.galleryFlag, overlaysApply);
      const colourLabel = (variant && variant.colour) || '';
      const sizeLabel = (variant && variant.size) || '';
      setText(this.refs.stepMeta, `${colourLabel} · ${sizeLabel}`);
      this.refs.colourLabels.forEach((node) => setText(node, colourLabel));
      this.refs.sizeLabels.forEach((node) => setText(node, sizeLabel));

      /* ---- personalisation ---- */
      setText(this.refs.nameCount, `${name.length}/${this.config.nameMax || 10}`);
      setText(this.refs.nameHint, name ? this.copy.hintValid : this.copy.hintEmpty);
      setText(this.refs.phoneCount, `${phone.length}/${this.config.phoneMax || 12}`);

      if (this.refs.phone) {
        this.refs.phone.disabled = nameOnly;
        if (nameOnly) this.refs.phone.value = '';
      }

      this.refs.patches.forEach((node) => node.classList.toggle('cph-patch--nameonly', nameOnly));
      this.refs.patchNames.forEach((node) => setText(node, name || this.copy.namePlaceholder));
      this.refs.patchPhones.forEach((node) => {
        setText(node, phone || this.copy.phonePlaceholder);
        show(node, !nameOnly);
      });

      /* ---- CTA ---- */
      const sellable = !variant || variant.available;
      if (this.refs.cta) this.refs.cta.disabled = !sellable;
      setText(
        this.refs.ctaLabel,
        sellable ? interpolate(this.copy.cta, { price: formatMoney(now, this.money) }) : this.copy.ctaSoldOut
      );

      this.broadcast({
        colour: variant && variant.colour,
        size: variant && variant.size,
        // What the shopper actually picked, which is what the size chart and
        // the colour grid mark themselves against. The resolved variant can
        // carry a different label when a card value and an option value have
        // drifted apart, and the grid should still follow the click.
        colourChoice: colour,
        sizeChoice: this.currentSize(),
        // Raw values drive the patch demo; the *Label pair carries the
        // placeholder so a summary further down the page never reads
        // "Patch reads BUSTER ·" with nothing after the separator.
        name,
        phone,
        nameLabel: name || this.copy.namePlaceholder,
        phoneLabel: phone || this.copy.phonePlaceholder,
        nameOnly,
        priceLabel: formatMoney(now, this.money),
        compareLabel: formatMoney(was, this.money),
        hasSaving,
        tierTitle: tier.title || '',
        available: sellable,
      });
    }

    broadcast(state) {
      window.cphState = state;
      document.dispatchEvent(new CustomEvent(STATE_EVENT, { bubbles: true, detail: state }));
    }
  }

  /* =========================================================================
     <cph-gallery> — product media, driven by the buy box.
     ========================================================================= */

  class CphGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.main = this.querySelector('[data-cph="gallery-main"]');
      this.thumbs = Array.from(this.querySelectorAll('[data-cph="thumb"]'));
      // Videos sit in the stage next to the photograph, one per video media
      // item, and the rail decides which of them is on show.
      this.videos = Array.from(this.querySelectorAll('[data-cph="stage-video"]'));
      this.zoom = this.querySelector('[data-cph="stage-zoom"]');
      const media = readConfig(this, 'data-cph-media') || {};
      this.map = media.colours || {};
      // Media the configuration overlays belong on: the main image and every
      // variant image. Everything else in the gallery is a diagram or a detail
      // shot that no colour-and-size chip describes.
      this.overlay = new Set((media.overlay || []).map(String));

      // Where the stage goes back to once the opening shot lets go: the
      // photograph the selected variant would have shown on its own.
      this.variantIndex = media.variant == null ? (media.overlay || [0])[0] : media.variant;
      this.index = String(media.initial == null ? this.variantIndex : media.initial);

      // An opening shot is a dog wearing the harness, not a colour swatch, so
      // it holds the stage only until the shopper does something that implies
      // they want to see the product itself.
      this.holding = media.opening === true;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          this.holding = false;
          this.showIndex(thumb.dataset.cphIndex);
          // Tell the buy box to re-evaluate the overlays for the new image.
          this.dispatchEvent(new CustomEvent('cph:media', { bubbles: true }));
        });
      });
    }

    /** Is the media at this rail position a video rather than a photograph? */
    isVideoIndex(index) {
      const thumb = this.thumbs.find((t) => t.dataset.cphIndex === String(index));
      const type = thumb ? thumb.dataset.cphMediaType : '';
      return type === 'video' || type === 'external_video';
    }

    showIndex(index) {
      const thumb = this.thumbs.find((t) => t.dataset.cphIndex === String(index));
      if (!thumb || !this.main) return;

      this.index = String(index);
      const isVideo = this.isVideoIndex(index);

      // Only one thing is ever on the stage. Arriving on a video starts it,
      // leaving one pauses it: a hidden <video> goes on playing, and a shopper
      // who taps to the next photograph should not still be hearing a page
      // they cannot see.
      this.videos.forEach((wrap) => {
        const on = isVideo && wrap.dataset.cphVideoIndex === String(index);
        // Whether this is an arrival or just a repaint of what is already on
        // the stage. Only an arrival starts playback — re-running the render
        // must not restart a video the shopper deliberately paused.
        const arriving = on && wrap.hidden;
        wrap.hidden = !on;

        const player = wrap.querySelector('video');
        // Null for an externally hosted video, which is an <iframe> this has
        // no reach into. There are none on this store; if one is ever added it
        // will need its own play/pause via the provider's embed parameters.
        if (!player) return;

        if (arriving) {
          /* Unmuted on purpose. Browsers block sound only on playback the
             shopper did not ask for; this runs inside the click on the video's
             own thumbnail, which is as explicit a request as there is, so it
             is allowed to play with its audio. play() still returns a promise
             that rejects if a browser disagrees — swallowed, because the
             controls are right there and a refused autoplay is not an error. */
          const started = player.play();
          if (started && typeof started.catch === 'function') started.catch(() => {});
        } else if (!on && !player.paused) {
          player.pause();
        }
      });

      // The photograph steps aside for a video rather than sitting behind it,
      // and takes its zoom button with it: the zoom opens a lightbox of still
      // frames, and a video has no still frame worth opening.
      const photo = this.zoom || this.main;
      photo.hidden = isVideo;

      if (!isVideo) {
        this.main.src = thumb.dataset.cphFull;
        this.main.srcset = thumb.dataset.cphSrcset || '';
        this.main.alt = thumb.dataset.cphAlt || '';
      }
      this.thumbs.forEach((t) => t.setAttribute('aria-current', t === thumb ? 'true' : 'false'));
    }

    /** Swap to the media assigned to a colour, when the admin has assigned one. */
    showColour(colour) {
      this.holding = false;
      const index = this.map[String(colour).toLowerCase()];
      if (index != null) this.showIndex(index);
    }

    /**
     * The shopper's first move — a size, a keystroke — hands the stage from the
     * opening shot to the variant photograph, and normal behaviour resumes for
     * the rest of the visit. Colour picks and thumbnail taps choose an image of
     * their own, so they never come through here.
     *
     * @param {string} [colour] The colour currently selected, if any.
     * @returns {boolean} Whether the opening shot was the one on screen.
     */
    releaseOpening(colour) {
      if (!this.holding) return false;
      this.holding = false;
      let index = colour == null ? null : this.map[String(colour).toLowerCase()];
      if (index == null) index = this.variantIndex;
      this.showIndex(index);
      return true;
    }

    /** Is the large image one the configuration overlays belong on? */
    isShowingOverlayMedia() {
      if (!this.overlay || this.overlay.size === 0) return false;
      return this.overlay.has(String(this.index));
    }
  }

  /* =========================================================================
     <cph-compare> — open a row to read the sentences behind the marks.
     ========================================================================= */

  class CphCompare extends HTMLElement {
    connectedCallback() {
      this.rows = Array.from(this.querySelectorAll('[data-cph="cmp-row"]'));
      this.rows.forEach((row) => {
        const toggle = row.querySelector('[data-cph="cmp-toggle"]');
        const detail = row.nextElementSibling;
        if (!toggle || !detail || detail.dataset.cph !== 'cmp-detail') return;
        toggle.addEventListener('click', () => {
          // Only one open at a time, so the table never becomes a wall of text.
          const opening = detail.hidden;
          this.closeAll();
          detail.hidden = !opening;
          row.classList.toggle('is-open', opening);
          toggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
        });
      });
    }

    closeAll() {
      this.rows.forEach((row) => {
        const detail = row.nextElementSibling;
        const toggle = row.querySelector('[data-cph="cmp-toggle"]');
        if (detail && detail.dataset.cph === 'cmp-detail') detail.hidden = true;
        row.classList.remove('is-open');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    }
  }

  /* =========================================================================
     <cph-scroller> — tells the reader a table has more to the right.
     ========================================================================= */

  class CphScroller extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.cph-scroller');
      if (!this.rail) return;

      this.onScroll = () => this.sync();
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.sync());
        this.observer.observe(this.rail);
      }
      this.sync();
    }

    disconnectedCallback() {
      if (this.rail) this.rail.removeEventListener('scroll', this.onScroll);
      if (this.observer) this.observer.disconnect();
    }

    sync() {
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      this.classList.toggle('is-scrollable', max > 4);
      this.classList.toggle('is-end', max <= 4 || this.rail.scrollLeft >= max - 4);
    }
  }

  /* =========================================================================
     <cph-lightbox> — product media in a modal, click to zoom.
     ========================================================================= */

  class CphLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-cph="lightbox"]');
      // No <dialog> support means no modal; the page keeps working without it.
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.img = this.querySelector('[data-cph="lightbox-img"]');
      this.counter = this.querySelector('[data-cph="lightbox-count"]');
      this.hint = this.querySelector('[data-cph="lightbox-hint"]');
      // Not `this.title`: HTMLElement already has a string `title` property,
      // so assigning an element to it stringifies and every later use fails.
      this.titleEl = this.querySelector('[data-cph="lightbox-title"]');
      this.captionEl = this.querySelector('[data-cph="lightbox-caption"]');
      this.nav = Array.from(this.querySelectorAll('[data-cph="lightbox-prev"], [data-cph="lightbox-next"]'));

      // The triggers live in the gallery, outside this element.
      const scope = this.closest('cph-gallery') || document;
      this.thumbs = Array.from(scope.querySelectorAll('[data-cph="thumb"]'));
      this.stage = scope.querySelector('[data-cph="stage-zoom"]');

      this.index = 0;
      this.frames = [];
      this.listeners = [];

      // Closing, the backdrop, zoom and the arrows belong to both modes: a
      // borrowed strip needs stepping as much as the gallery does. Only opening
      // from the large image needs a gallery to exist.
      this.bindCommon();
      if (this.thumbs.length) this.bind();
    }

    disconnectedCallback() {
      this.listeners.forEach(([t, type, fn]) => t.removeEventListener(type, fn));
      this.listeners = [];
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    on(target, type, fn) {
      if (!target) return;
      target.addEventListener(type, fn);
      this.listeners.push([target, type, fn]);
    }

    /** Wiring both modes share. */
    bindCommon() {
      this.on(this.querySelector('[data-cph="lightbox-close"]'), 'click', () => this.dialog.close());

      // Clicking the backdrop resolves to the dialog element itself.
      this.on(this.dialog, 'click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      this.on(this.dialog, 'close', () => {
        this.setZoom(false);
        // <dialog> restores focus itself, but only to whatever opened it. A
        // borrowed opener says where it wants focus back.
        if (this.returnTo && typeof this.returnTo.focus === 'function') {
          const target = this.returnTo;
          this.returnTo = null;
          target.focus();
        }
      });

      this.on(this.querySelector('[data-cph="lightbox-prev"]'), 'click', () => this.step(-1));
      this.on(this.querySelector('[data-cph="lightbox-next"]'), 'click', () => this.step(1));

      /* Swipe between images on a touch screen. The arrows were the only way
         through the strip, and on a phone that is a pair of small targets over
         a full-screen photo.

         Ignored while zoomed: a drag then means panning around the enlarged
         image, and paging out from under the customer's finger would be the
         opposite of what they asked for. Ignored for multi-touch for the same
         reason — that is a pinch. */
      const MIN_X = 45;
      let x0 = null;
      let y0 = null;

      this.on(this.dialog, 'touchstart', (event) => {
        if (this.zoomed || event.touches.length !== 1) {
          x0 = null;
          return;
        }
        x0 = event.touches[0].clientX;
        y0 = event.touches[0].clientY;
      });

      this.on(this.dialog, 'touchend', (event) => {
        if (x0 === null || this.zoomed) return;
        const touch = event.changedTouches && event.changedTouches[0];
        const dx = touch ? touch.clientX - x0 : 0;
        const dy = touch ? touch.clientY - y0 : 0;
        x0 = null;
        y0 = null;
        if (Math.abs(dx) < MIN_X || Math.abs(dx) <= Math.abs(dy)) return;
        this.step(dx < 0 ? 1 : -1);
      });

      // Click to zoom toward the point clicked, then track the pointer to pan.
      this.on(this.img, 'click', (event) => {
        event.stopPropagation();
        this.setZoom(!this.zoomed, event);
      });
      this.on(this.img, 'mousemove', (event) => {
        if (this.zoomed) this.originFrom(event);
      });
    }

    bind() {
      // Clicking the large image opens whatever it is currently showing.
      this.on(this.stage, 'click', () => {
        const current = this.thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true');
        this.open(current < 0 ? 0 : current);
      });

      // No `single` guard here any more: step() decides for itself whether
      // there is anywhere to go, so the arrow keys walk a borrowed strip and do
      // nothing on a lone picture, which is the answer the buttons give too.
      this.on(this.dialog, 'keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
      });
    }

    /**
     * The gallery strip as frames, so both modes step the same shape.
     *
     * Video frames are marked rather than dropped: the frame list is indexed
     * by rail position, so removing one would slide every later photograph one
     * place out of step with its thumbnail. step() walks past them instead.
     */
    galleryFrames() {
      return this.thumbs.map((thumb) => ({
        src: thumb.dataset.cphFull || '',
        alt: thumb.dataset.cphAlt || '',
        w: Number(thumb.dataset.cphW),
        h: Number(thumb.dataset.cphH),
        video:
          thumb.dataset.cphMediaType === 'video' ||
          thumb.dataset.cphMediaType === 'external_video',
      }));
    }

    /**
     * Show a set of pictures that has nothing to do with the product gallery —
     * the lead's colours, a size diagram.
     *
     * A borrowed view brings its own frames. The arrows used to be hidden here
     * and left pointing at the gallery underneath, so stepping on from a lead
     * photo landed on the harness anatomy diagram: a different product, with no
     * way back to the one that had actually been opened. Now the strip travels
     * with the caller and the arrows walk that.
     *
     * @param {{src: string, alt: string, title: string, caption: string,
     *          returnTo: Element,
     *          items: Array<{src: string, alt: string, title: string,
     *                        w: number, h: number}>,
     *          index: number}} options
     */
    openSingle(options) {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      const opts = options || {};

      this.single = true;
      this.returnTo = opts.returnTo || null;
      this.caption = opts.caption || '';
      this.frames =
        opts.items && opts.items.length
          ? opts.items
          : [{ src: opts.src || '', alt: opts.alt || '', title: opts.title || '' }];

      // One picture keeps the arrows and the counter off; a borrowed strip earns
      // both, because there is somewhere for them to go.
      const many = this.frames.length > 1;
      if (this.counter) this.counter.hidden = !many;
      this.nav.forEach((n) => { n.hidden = !many; });
      if (this.hint) this.hint.hidden = true;

      const start = Number(opts.index) || 0;
      this.show(clamp(0, start, this.frames.length - 1));
      this.setZoom(false);
      if (!this.dialog.open) this.dialog.showModal();
    }

    open(index) {
      // Coming back from a borrowed view, restore what gallery mode owns.
      this.single = false;
      this.returnTo = null;
      this.caption = '';
      this.frames = this.galleryFrames();
      if (this.captionEl) this.captionEl.hidden = true;
      if (this.counter) this.counter.hidden = false;
      if (this.hint) this.hint.hidden = false;
      this.nav.forEach((n) => { n.hidden = false; });

      this.show(index);
      if (!this.dialog.open) this.dialog.showModal();
    }

    step(delta) {
      const frames = this.frames || [];
      if (frames.length < 2) return;
      this.setZoom(false);
      // Walk past video frames: the lightbox shows stills, and a video's frame
      // is only its poster, so landing on one looks like a picture that will
      // not zoom. Bounded by the frame count so a strip that is somehow all
      // video stops rather than spinning.
      let next = this.index;
      for (let hops = 0; hops < frames.length; hops += 1) {
        next = (next + delta + frames.length) % frames.length;
        if (!frames[next] || !frames[next].video) break;
      }
      if (frames[next] && frames[next].video) return;
      this.show(next);
    }

    /** Put the transform origin under the pointer so zoom lands where you aim. */
    originFrom(event) {
      const box = this.img.getBoundingClientRect();
      const x = ((event.clientX - box.left) / box.width) * 100;
      const y = ((event.clientY - box.top) / box.height) * 100;
      this.img.style.transformOrigin = `${clamp(0, x, 100)}% ${clamp(0, y, 100)}%`;
    }

    setZoom(on, event) {
      this.zoomed = !!on;
      if (on && event) this.originFrom(event);
      if (!on) this.img.style.transformOrigin = 'center center';
      this.img.classList.toggle('is-zoomed', this.zoomed);
      this.dialog.classList.toggle('is-zoomed', this.zoomed);
      show(this.hint, !this.zoomed);
    }

    show(index) {
      const frames = this.frames || [];
      const frame = frames[index];
      if (!frame || !this.img) return;
      this.index = index;

      // Set the intrinsic size before the source so the browser reserves the
      // right aspect ratio instead of the placeholder square in the markup.
      if (frame.w > 0 && frame.h > 0) {
        this.img.width = frame.w;
        this.img.height = frame.h;
      }
      this.img.src = frame.src || '';
      this.img.srcset = '';
      this.img.alt = frame.alt || '';

      // The title is per frame, so a strip of colours names the one on screen.
      // The caption describes the borrowed set as a whole, so it does not move.
      const title = this.single ? frame.title || '' : '';
      setText(this.titleEl, title);
      if (this.titleEl) this.titleEl.hidden = !title;
      setText(this.captionEl, this.single ? this.caption : '');
      if (this.captionEl) this.captionEl.hidden = !(this.single && this.caption);

      if (this.counter) this.counter.textContent = `${index + 1} / ${frames.length}`;
    }
  }

  /* =========================================================================
     <cph-rail> — the thumbnail strip, paged by its own arrows.
     ========================================================================= */

  class CphRail extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-cph="rail"]');
      this.prev = this.querySelector('[data-cph="rail-prev"]');
      this.next = this.querySelector('[data-cph="rail-next"]');
      if (!this.rail) return;

      this.onPrev = () => this.page(-1);
      this.onNext = () => this.page(1);
      this.onScroll = () => this.sync();

      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.sync());
        this.observer.observe(this.rail);
      }
      this.sync();
    }

    disconnectedCallback() {
      if (this.prev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next) this.next.removeEventListener('click', this.onNext);
      if (this.rail) this.rail.removeEventListener('scroll', this.onScroll);
      if (this.observer) this.observer.disconnect();
    }

    /** Scroll by one visible page, leaving a tile of overlap for context. */
    page(direction) {
      const step = Math.max(80, this.rail.clientWidth * 0.8);
      this.rail.scrollBy({ left: step * direction, behavior: 'smooth' });
    }

    /** Arrows only exist while there is somewhere left to scroll. */
    sync() {
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      const overflowing = max > 4;
      const x = this.rail.scrollLeft;
      if (this.prev) this.prev.hidden = !overflowing || x <= 4;
      if (this.next) this.next.hidden = !overflowing || x >= max - 4;
    }
  }

  /* =========================================================================
     <cph-faq> — one open answer at a time.
     ========================================================================= */

  class CphFaq extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.items = Array.from(this.querySelectorAll('[data-cph="faq-item"]'));
      this.items.forEach((item) => {
        const button = item.querySelector('[data-cph="faq-q"]');
        if (!button) return;
        button.addEventListener('click', () => this.toggle(item));
      });
    }

    toggle(target) {
      const answer = target.querySelector('[data-cph="faq-a"]');
      const opening = answer && answer.hidden;

      this.items.forEach((item) => {
        const a = item.querySelector('[data-cph="faq-a"]');
        const q = item.querySelector('[data-cph="faq-q"]');
        const sign = item.querySelector('[data-cph="faq-sign"]');
        const open = item === target && opening;
        if (a) a.hidden = !open;
        if (q) q.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (sign) sign.textContent = open ? '−' : '+';
      });
    }
  }

  /* =========================================================================
     <cph-patchswap> — message demo plus the spare patch upsell.
     ========================================================================= */

  class CphPatchswap extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.config = readConfig(this, 'data-cph-config') || {};
      this.copy = this.config.copy || {};
      this.money = this.config.moneyFormat || '${{amount}}';

      this.buttons = Array.from(this.querySelectorAll('[data-cph="msg"]'));
      this.line1 = this.querySelector('[data-cph="demo-l1"]');
      this.line2 = this.querySelector('[data-cph="demo-l2"]');
      this.note = this.querySelector('[data-cph="demo-note"]');

      this.spares = Array.from(this.querySelectorAll('[data-cph="spare"]'));
      this.addButton = this.querySelector('[data-cph="spares-add"]');

      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.showMessage(button));
      });

      this.spares.forEach((spare) => {
        spare.addEventListener('click', () => {
          const on = spare.getAttribute('aria-pressed') === 'true';
          spare.setAttribute('aria-pressed', on ? 'false' : 'true');
          spare.classList.toggle('is-on', !on);
          const tick = spare.querySelector('[data-cph="spare-tick"]');
          setText(tick, on ? spare.dataset.cphOff : spare.dataset.cphOn);
          this.renderAdd();
        });
      });

      if (this.addButton) {
        this.addButton.addEventListener('click', () => this.addSpares());
      }

      // The first message keeps the state the server rendered. Everything after
      // that comes from a click.
      const first = this.buttons.find((b) => b.getAttribute('aria-pressed') === 'true') || this.buttons[0];
      if (first) this.showMessage(first);
      this.renderAdd();

      // Until a name has been typed the demo shows whatever the editor set. Once
      // the buy box broadcasts, the name-and-number message uses the real name.
      document.addEventListener(STATE_EVENT, (event) => {
        this.state = event.detail;
        const active = this.buttons.find((b) => b.getAttribute('aria-pressed') === 'true');
        if (active && active.dataset.cphLive === 'true') this.showMessage(active);
      });
      if (window.cphState) this.state = window.cphState;
    }

    showMessage(button) {
      this.buttons.forEach((b) => {
        b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
        b.classList.toggle('is-active', b === button);
      });

      let l1 = button.dataset.cphL1 || '';
      let l2 = button.dataset.cphL2 || '';

      if (button.dataset.cphLive === 'true' && this.state) {
        l1 = this.state.name || l1;
        l2 = this.state.nameOnly ? '' : this.state.phone || l2;
      }

      setText(this.line1, l1);
      setText(this.line2, l2);
      show(this.line2, !!l2);
      if (this.line1) this.line1.classList.toggle('is-long', l1.length > 11);
      setText(this.note, button.dataset.cphNote || '');
    }

    selected() {
      return this.spares.filter((s) => s.getAttribute('aria-pressed') === 'true');
    }

    renderAdd() {
      if (!this.addButton) return;
      const chosen = this.selected();
      const total = chosen.reduce((sum, s) => sum + (Number(s.dataset.cphPrice) || 0), 0);

      this.addButton.disabled = chosen.length === 0;
      setText(
        this.addButton,
        chosen.length
          ? interpolate(chosen.length > 1 ? this.copy.addManySpares : this.copy.addOneSpare, {
              count: chosen.length,
              total: formatMoney(total, this.money),
            })
          : this.copy.chooseSpare
      );
    }

    addSpares() {
      const chosen = this.selected();
      if (!chosen.length) return;

      const items = chosen
        .filter((s) => s.dataset.cphVariant)
        .map((s) => ({
          id: Number(s.dataset.cphVariant),
          quantity: 1,
          properties: s.dataset.cphProperty ? { [this.config.propertyKey]: s.dataset.cphProperty } : undefined,
        }));

      if (!items.length) {
        setText(this.addButton, this.copy.spareUnavailable);
        return;
      }

      this.addButton.disabled = true;
      addToCart(items)
        .then(() => {
          setText(this.addButton, this.copy.sparesAdded);
        })
        .catch(() => {
          this.addButton.disabled = false;
          setText(this.addButton, this.copy.spareError);
        });
    }
  }

  /* =========================================================================
     <cph-picker> — size chart rows and colour cards that drive the buy box.
     ========================================================================= */

  class CphPicker extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.options = Array.from(this.querySelectorAll('[data-cph-pick]'));
      this.options.forEach((option) => {
        option.addEventListener('click', () => {
          const detail = {};
          if (option.dataset.cphSize) detail.size = option.dataset.cphSize;
          if (option.dataset.cphColour) detail.colour = option.dataset.cphColour;
          document.dispatchEvent(new CustomEvent(SELECT_EVENT, { bubbles: true, detail }));

          const buybox = document.getElementById(this.dataset.cphTarget || 'cph-buy');
          if (buybox && this.dataset.cphScroll !== 'false') {
            buybox.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      });

      // A section that renders after the buy box has already broadcast would
      // otherwise sit on the server-rendered selection forever.
      document.addEventListener(STATE_EVENT, (event) => this.mark(event.detail));
      if (window.cphState) this.mark(window.cphState);
    }

    mark(state) {
      if (!state) return;
      const matches = (value, primary, fallback) => {
        const wanted = String(value).toLowerCase();
        return wanted === String(primary || '').toLowerCase()
          || wanted === String(fallback || '').toLowerCase();
      };

      this.options.forEach((option) => {
        const isSize = option.dataset.cphSize
          && matches(option.dataset.cphSize, state.sizeChoice, state.size);
        const isColour = option.dataset.cphColour
          && matches(option.dataset.cphColour, state.colourChoice, state.colour);
        const on = !!(isSize || isColour);

        const row = option.closest('[data-cph="chart-row"]') || option;
        row.classList.toggle('is-selected', on);
        option.classList.toggle('is-active', on);

        const label = option.querySelector('[data-cph="pick-label"]');
        if (label) setText(label, on ? option.dataset.cphOn : option.dataset.cphOff);
      });
    }
  }

  /* =========================================================================
     <cph-recap> — the closing card mirrors the buy box.
     ========================================================================= */

  class CphRecap extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.copy = readConfig(this, 'data-cph-config') || {};
      this.refs = {
        title: this.querySelector('[data-cph="recap-title"]'),
        sub: this.querySelector('[data-cph="recap-sub"]'),
        now: this.querySelector('[data-cph="recap-now"]'),
        was: this.querySelector('[data-cph="recap-was"]'),
        cta: this.querySelector('[data-cph="recap-cta"]'),
      };

      document.addEventListener(STATE_EVENT, (event) => this.render(event.detail));
      if (window.cphState) this.render(window.cphState);
    }

    render(state) {
      if (!state) return;

      setText(
        this.refs.title,
        interpolate(this.copy.title, { tier: state.tierTitle, colour: state.colour, size: state.size })
      );
      const name = state.nameLabel || state.name;
      const phone = state.phoneLabel || state.phone;
      setText(
        this.refs.sub,
        state.nameOnly
          ? interpolate(this.copy.subNameOnly, { name })
          : interpolate(this.copy.sub, { name, phone })
      );
      setText(this.refs.now, state.priceLabel);
      setText(this.refs.was, state.compareLabel);
      show(this.refs.was, state.hasSaving);
      if (this.refs.cta && this.copy.cta) {
        setText(this.refs.cta, interpolate(this.copy.cta, { price: state.priceLabel }));
      }
    }
  }

  /* --------------------------------------------------------------- define */

  /* ------------------------------------------------------------------------
     Lead price — the copy shown under the subtitle.

     A mirror of the price at the foot of the buy form, never a second
     calculation: bundles, spare-patch add-ons and per-variant prices are all
     worked out in one place, and this only repeats the answer higher up the
     page where the shopper sees it before the configurator.

     It copies the node rather than reading cphState or waiting for a broadcast.
     The price is recomputed during init — the selected tier is not necessarily
     the bare variant price — and a subscriber can miss that first write, which
     would leave one price at the top of the page and a different one at the
     bottom. The initial copy covers an init that has already run; the observer
     covers one that runs afterwards.
     --------------------------------------------------------------------- */
  const wireLeadPrice = (box) => {
    if (box.dataset.cphLeadReady === 'on') return;

    const scope = box.closest('.cph-buybox') || document;
    const sourceNow = scope.querySelector('[data-cph="price-now"]');
    if (!sourceNow) return;

    box.dataset.cphLeadReady = 'on';

    const sourceWas = scope.querySelector('[data-cph="price-was"]');
    const sourceSave = scope.querySelector('[data-cph="price-save"]');
    const now = box.querySelector('[data-cph="leadprice-now"]');
    const was = box.querySelector('[data-cph="leadprice-was"]');
    const save = box.querySelector('[data-cph="leadprice-save"]');

    const copy = () => {
      if (now) now.textContent = sourceNow.textContent;
      if (was && sourceWas) {
        was.textContent = sourceWas.textContent;
        was.hidden = sourceWas.hidden;
      }
      /* The saving travels with its own hidden flag: a tier with no compare-at
         price has none to state, and the pill has to go rather than sit there
         empty. */
      if (save && sourceSave) {
        save.textContent = sourceSave.textContent;
        save.hidden = sourceSave.hidden;
      }
    };

    copy();

    if (!window.MutationObserver) return;
    const observer = new MutationObserver(copy);
    const watch = { childList: true, characterData: true, subtree: true };
    observer.observe(sourceNow, watch);
    if (sourceWas) {
      observer.observe(sourceWas, { ...watch, attributes: true, attributeFilter: ['hidden'] });
    }
    if (sourceSave) {
      observer.observe(sourceSave, { ...watch, attributes: true, attributeFilter: ['hidden'] });
    }
  };

  const bootLeadPrices = () => {
    document.querySelectorAll('[data-cph="leadprice"]').forEach(wireLeadPrice);
  };

  /* ---- Photo swatch row ---------------------------------------------------
     On a phone the thirteen colour tiles are one scrolling row rather than five
     rows of grid. Two things follow from that and neither is a style.

     One: the checked tile can start off screen. Graffiti black is the default
     and it sits eleventh, so the row opened on four colours with nothing
     selected among them, which reads as no colour chosen at all. The row is
     scrolled — not the page, and not with scrollIntoView, which would take the
     document with it — so the shopper's current colour is on screen from the
     first paint.

     Two: once it is scrolled, the cut tile at the right is no longer the only
     edge with more behind it. The state goes on the fieldset and the fades read
     it, so each one only appears when there is actually something under it. */
  const wireSwatchRow = (row) => {
    if (row.dataset.cphRowReady === 'on') return;
    row.dataset.cphRowReady = 'on';

    const field = row.closest('.cph-fieldset--swipe') || row.parentElement;

    const mark = () => {
      const max = row.scrollWidth - row.clientWidth;
      /* A row that does not overflow gets neither fade. The 2px allows for the
         sub-pixel width the browser reports at some zoom levels. */
      const state = max <= 2 ? 'none' : row.scrollLeft <= 2 ? 'start' : row.scrollLeft >= max - 2 ? 'end' : 'both';
      field.setAttribute('data-cph-row', state);
    };

    const reveal = () => {
      const checked = row.querySelector('.cph-pill__input:checked');
      const tile = checked && checked.nextElementSibling;
      if (tile) {
        const want = tile.offsetLeft - (row.clientWidth - tile.offsetWidth) / 2;
        row.scrollLeft = Math.max(0, Math.min(want, row.scrollWidth - row.clientWidth));
      }
      mark();
    };

    reveal();
    row.addEventListener('scroll', mark, { passive: true });
    window.addEventListener('resize', reveal, { passive: true });
    /* The checked tile moves when the shopper picks a colour off the edge of
       the row; bringing it back to the middle is what a swipe would have done. */
    row.addEventListener('change', () => window.requestAnimationFrame(reveal));
  };

  const bootSwatchRows = () => {
    document.querySelectorAll('.cph-pills--photo').forEach(wireSwatchRow);
  };


  const bootExtras = () => {
    bootLeadPrices();
    bootSwatchRows();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootExtras, { once: true });
  } else {
    bootExtras();
  }
  document.addEventListener('shopify:section:load', bootExtras);

  const define = (tag, klass) => {
    if (!customElements.get(tag)) customElements.define(tag, klass);
  };

  // Gallery before buy box: defining an element upgrades matching nodes
  // immediately, and the buy box's first render asks the gallery what it is
  // showing. Reversed, that call lands on a plain HTMLElement.
  define('cph-gallery', CphGallery);
  define('cph-buybox', CphBuybox);
  define('cph-rail', CphRail);
  define('cph-scroller', CphScroller);
  define('cph-compare', CphCompare);
  define('cph-lightbox', CphLightbox);
  define('cph-faq', CphFaq);
  define('cph-patchswap', CphPatchswap);
  define('cph-picker', CphPicker);
  define('cph-recap', CphRecap);
})();
